import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PanelPlacementType } from 'src/types/decorationSpecs';
import { changeEditorSkuSuccess, clearEditorSku } from 'src/pages/editor/components/surfaces/slice';
import type { GenericSelectorProductType } from 'src/types/product';

export type DecorationSpecsSliceState = {
    /** Identity of the product/selection the current data (or in-flight fetch) applies to —
     * `${sku}-${skuVersion}-${merchantSku}-${merchantVersion}-${JSON.stringify(merchantVariables)}`.
     * Used to skip redundant refetches and to ignore stale responses from a superseded request.
     * Covers `decorationPlacement`/`printWhite` (DIS) only. */
    cacheKey: string | undefined;
    isLoading: boolean;
    /** `null` means DIS confirmed there's no data for this product (e.g. non-apparel) — distinct
     * from not having fetched yet (`cacheKey === undefined`). */
    decorationPlacement: PanelPlacementType[] | null;
    printWhite: boolean | null;
    /** Cache key for the fetched product model definition — `${effectiveSku}-${effectiveVersion}`. */
    productModelKey: string | undefined;
    /** The raw product definition fetched from api.products.cimpress.io (containing options and property assignments
     * for all sizes). Used by selectors (`getSizeChart`, `getBodyMeasurements`) to derive size chart and
     * body width/length dynamically against the current merchantVariables. */
    productModel: GenericSelectorProductType | null;
    /** Product options (e.g. `Backside`, `Decoration Technology`) still unset that are needed
     * before `decorationPlacement`/`printWhite` can be trusted — populated instead of those fields
     * when this is why they're absent/unreliable, so the UI can prompt for a selection rather than
     * showing "not available" or a possibly-wrong single answer. Computed proactively from the
     * product's own declared options (see `unresolvedProductOptions.ts`), not solely from
     * whatever DIS itself reports, since DIS's own "needs more info" signaling is inconsistent. */
    decorationMissingSelections: string[] | null;
    error?: string;
};

export const initialState: DecorationSpecsSliceState = {
    cacheKey: undefined,
    isLoading: false,
    decorationPlacement: null,
    printWhite: null,
    productModelKey: undefined,
    productModel: null,
    decorationMissingSelections: null,
    error: undefined,
};

type RetrieveDecorationSpecsPayload = { cacheKey: string };

type RetrieveDecorationSpecsSettledPayload = {
    cacheKey: string;
    decorationPlacement: PanelPlacementType[] | null;
    printWhite: boolean | null;
    decorationMissingSelections: string[] | null;
    error?: string;
};

type RequestProductModelPayload = { productModelKey: string };

type ProductModelResolvedPayload = {
    productModelKey: string;
    productModel: GenericSelectorProductType | null;
};

const slice = createSlice({
    name: 'decorationSpecs',
    initialState,
    reducers: {
        retrieveDecorationSpecs: (state, { payload }: PayloadAction<RetrieveDecorationSpecsPayload>) => {
            state.cacheKey = payload.cacheKey;
            state.isLoading = true;
            state.error = undefined;
            // Clear the previous DIS result immediately, not just when the new one settles. The
            // previous result describes the *old* selection — if a designer removes or changes an
            // option that made it valid (e.g. clearing `Backside` after DIS had already answered
            // for a fully-specified selection), that answer is no longer trustworthy and must not
            // keep rendering as if it still applies for however long this fetch takes.
            //
            // Deliberately does NOT clear `productModel` — it stays available across a DIS refetch.
            state.decorationPlacement = null;
            state.printWhite = null;
            state.decorationMissingSelections = null;
        },
        retrieveDecorationSpecsSettled: (state, { payload }: PayloadAction<RetrieveDecorationSpecsSettledPayload>) => {
            // Guard against a slower, now-superseded request resolving after a newer selection
            // change already started a different fetch.
            if (state.cacheKey !== payload.cacheKey) {
                return;
            }
            state.isLoading = false;
            state.decorationPlacement = payload.decorationPlacement;
            state.printWhite = payload.printWhite;
            state.decorationMissingSelections = payload.decorationMissingSelections;
            state.error = payload.error;
        },
        /**
         * Marks a product model fetch as in flight for `productModelKey`, set optimistically
         * *before* the request resolves — mirrors `retrieveDecorationSpecs`'s `cacheKey` handling.
         * This lets `productModelResolved` reject a slower, now-superseded response the same way
         * `retrieveDecorationSpecsSettled` already rejects a superseded `cacheKey`: without it, two
         * overlapping fetches (e.g. two rapid SKU changes before the first request resolves) could
         * resolve out of order and leave a stale product model — and the body measurements derived
         * from it — applied to whatever SKU is currently selected.
         */
        requestProductModel: (state, { payload }: PayloadAction<RequestProductModelPayload>) => {
            state.productModelKey = payload.productModelKey;
            // Clear the previous model immediately, not just when the new one settles — it
            // describes a different product identity now that `productModelKey` has changed.
            state.productModel = null;
        },
        /**
         * Sets the product model definition fetched from `api.products.cimpress.io`.
         */
        productModelResolved: (state, { payload }: PayloadAction<ProductModelResolvedPayload>) => {
            // Guard against a slower, now-superseded request resolving after a newer product/version
            // change already started a different fetch (see `requestProductModel`).
            if (state.productModelKey !== payload.productModelKey) {
                return;
            }
            state.productModel = payload.productModel;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(clearEditorSku, () => initialState).addCase(changeEditorSkuSuccess, () => initialState);
    },
});

export const reducer = slice.reducer;
export const { retrieveDecorationSpecs, retrieveDecorationSpecsSettled, requestProductModel, productModelResolved } =
    slice.actions;
